import { z } from 'zod';
import { SiteMode } from '../enums.js';

/**
 * Storefront availability configuration (admin-managed).
 * When mode is not "live" the public storefront + public API are gated;
 * staff sessions and a shareable preview token bypass the gate.
 */
export const comingSoonContentSchema = z.object({
  headline: z.string().max(120).default('Something is being made'),
  message: z.string().max(1000).default(''),
  launchAt: z.string().datetime().nullable().optional(),
  showCountdown: z.boolean().default(true),
  showNewsletter: z.boolean().default(true),
});

export const maintenanceContentSchema = z.object({
  headline: z.string().max(120).default('Down for maintenance'),
  message: z.string().max(1000).default('We are making some improvements and will be back shortly.'),
  estimatedBackAt: z.string().datetime().nullable().optional(),
});

export const siteStatusSchema = z.object({
  mode: z.enum([SiteMode.LIVE, SiteMode.COMING_SOON, SiteMode.MAINTENANCE]).default(SiteMode.LIVE),
  comingSoon: comingSoonContentSchema.partial().optional(),
  maintenance: maintenanceContentSchema.partial().optional(),
});

export type SiteStatusInput = z.infer<typeof siteStatusSchema>;

export interface ComingSoonContent {
  headline: string;
  message: string;
  launchAt: string | null;
  showCountdown: boolean;
  showNewsletter: boolean;
}

export interface MaintenanceContent {
  headline: string;
  message: string;
  estimatedBackAt: string | null;
}

/** Public shape returned by GET /content/site-status. */
export interface SiteStatusDTO {
  mode: SiteMode;
  comingSoon: ComingSoonContent;
  maintenance: MaintenanceContent;
  serverTime: string;
  /** How (if at all) the current viewer bypasses the gate while it is active. */
  bypass: 'none' | 'staff' | 'token';
}

/** Admin shape (adds the preview token + who/when). */
export interface AdminSiteStatusDTO extends SiteStatusDTO {
  previewToken: string;
  previewUrlParam: string;
  updatedAt: string | null;
}
